/* eslint-disable no-unsafe-optional-chaining */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Div } from '@pinpoint/design-kit';
// import { Fairness } from '@pinpoint/onpoint-charts';
import { ModelProp } from 'src/store/typings';
import ReactEcharts from 'echarts-for-react';
import DefaultTheme from 'src/styles/DefaultTheme';
import CONSTANTS from 'src/store/constants';
import { formatNumberV2, formatPercent } from 'src/functions';

const {
  colors: { fairnessActual, brand100, ...otherColors },
} = DefaultTheme;

// TODO: Move the infographic to charts after fully completed
export default function Infographic({ model, showValue, showPricing, demoIndex, minVal, maxVal }) {
  const dataset = model?.modelData?.dataPayload?.fairness;
  const isNotFreq = model?.dataModelType !== CONSTANTS.TAL_MODEL.TAG.ACT_FREQUENCY;
  const showDecimal = !isNotFreq || (isNotFreq && !showValue);
  const hasPricing = !!dataset?.dimensions?.find?.((val) => val === 'Actual Pricing AIR');
  const rmseIdx = dataset.dimensions?.findIndex?.((val) => val === 'RMSE');
  const stddevIdx = dataset.dimensions?.findIndex?.((val) => val === 'STDDEV');
  const countsIdx = dataset.dimensions?.findIndex?.((val) => val === 'Counts');
  const percIdx = dataset.dimensions?.findIndex?.((val) => val === 'Percentage');
  const maxY = useMemo(() => {
    if (showDecimal) return null;
    const actualValues = dataset?.source?.[1];
    const predictedValues = dataset?.source?.[2];
    const pricingValues = dataset?.source?.[3];
    const max = Math.max(...pricingValues, ...actualValues, ...predictedValues);
    const fixed = max?.toFixed?.(0);
    const order = fixed.length - 1;
    const valuePlus = 10 ** order;
    return Math.round(+fixed / valuePlus) * valuePlus;
  }, [showDecimal, dataset]);

  const yAxisName = useMemo(() => {
    let name = 'Claims Frequency';
    name = model.dataModelType === CONSTANTS.TAL_MODEL.TAG.ACT_SEVERITY ? 'Claims Severity' : name;
    name = model.dataModelType === CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_MULT ? 'Multiplied Loss Cost' : name;
    name = model.dataModelType === CONSTANTS.TAL_MODEL.TAG.ACT_LOSS_DIRECT ? 'Direct Loss Cost' : name;
    return name;
  }, [model.dataModelType]);
  const options = useMemo(() => {
    const modelType = model.dataModelType;
    const outData = dataset.source.map((src) =>
      src.slice(CONSTANTS.TAL_MODEL.DEMO_INDICES[demoIndex][0], CONSTANTS.TAL_MODEL.DEMO_INDICES[demoIndex][1])
    );
    let series1Idx;
    if (showValue) {
      series1Idx = 1;
    } else if (hasPricing) {
      series1Idx = 4;
    } else {
      series1Idx = 3;
    }
    let series2Idx;
    if (showValue) series2Idx = 2;
    else if (hasPricing) series2Idx = 5;
    else series2Idx = 4;
    const demoIdx = 0;
    const series1 =
      modelType === 'CLASSIFIER'
        ? {
            type: 'bar',
            seriesLayoutBy: 'row',
            name: dataset.dimensions[1],
            itemStyle: {
              color: fairnessActual,
              barBorderRadius: [5, 5, 0, 0],
            },
            encode: {
              y: 1,
            },
            tooltip: {
              backgroundColor: 'rgba(50, 50, 50, 0.9)',
              padding: 16,
              formatter: ({ data }) =>
                `${data?.[demoIdx]}<br />Number: ${formatNumberV2(data?.[3])}<br />Percentage: ${formatPercent(
                  data?.[percIdx] * 100,
                  false
                )}<br />Average Value: ${(+data?.[6] * 1000)?.toFixed?.(1)}<br />Std Dev: ${(
                  +data?.[stddevIdx] * 1000
                )?.toFixed?.(1)}`,
            },
            label: {
              show: true,
              position: 'insideTop',
              formatter: ({ data }) => `${+data?.[1]?.toFixed?.(2)}`,
              offset: [0, 8],
            },
          }
        : {
            type: 'bar',
            seriesLayoutBy: 'row',
            name: dataset.dimensions[series1Idx].replace('cost', 'Cost'),
            itemStyle: {
              color: fairnessActual,
              barBorderRadius: [5, 5, 0, 0],
            },
            encode: {
              y: series1Idx,
            },
            tooltip: {
              backgroundColor: 'rgba(50, 50, 50, 0.9)',
              padding: 16,
              formatter: ({ data }) =>
                `${data?.[demoIdx]}<br />Number: ${formatNumberV2(data?.[countsIdx])}<br />Percentage: ${formatPercent(
                  data?.[percIdx] * 100,
                  false
                )}<br />Average Value: ${+data?.[series1Idx]?.toFixed?.(3)}<br />Std Dev: ${data?.[
                  stddevIdx
                ]?.toFixed?.(3)}`,
            },
            label: {
              show: true,
              position: 'insideTop',
              formatter: ({ data }) =>
                showDecimal ? `${+data?.[series1Idx]?.toFixed?.(2)}` : `$${formatNumberV2(+data?.[series1Idx], true)}`,
              offset: [0, 8],
            },
          };
    const series2 =
      modelType === 'CLASSIFIER'
        ? {
            type: 'bar',
            seriesLayoutBy: 'row',
            name: dataset.dimensions[2],
            itemStyle: {
              color: otherColors[CONSTANTS.TAL_MODEL.ACT_STYLING[model.dataModelType].COLOR],
              barBorderRadius: [5, 5, 0, 0],
            },
            encode: {
              y: 2,
            },
            label: {
              show: true,
              position: 'insideTop',
              formatter: ({ data }) => +data?.[2]?.toFixed?.(2),
              offset: [0, 8],
            },
            tooltip: {
              backgroundColor: 'rgba(50, 50, 50, 0.9)',
              padding: 16,
              formatter: ({ data }) =>
                `${data?.[demoIdx]}<br />Number: ${formatNumberV2(data?.[countsIdx])}<br />Percentage: ${formatPercent(
                  data?.[percIdx] * 100,
                  false
                )}<br />Average Value: ${(+data?.[6] * 1000)?.toFixed?.(1)}<br />Std Dev: ${(
                  +data?.[stddevIdx] * 1000
                )?.toFixed?.(1)}`,
            },
          }
        : {
            type: 'bar',
            seriesLayoutBy: 'row',
            name: dataset.dimensions[series2Idx].replace('cost', 'Cost'),
            itemStyle: {
              color: otherColors[CONSTANTS.TAL_MODEL.ACT_STYLING[model.dataModelType].COLOR],
              barBorderRadius: [5, 5, 0, 0],
            },
            encode: {
              y: series2Idx,
            },
            label: {
              show: true,
              position: 'insideTop',
              formatter: ({ data }) =>
                showDecimal ? `${+data?.[series2Idx]?.toFixed?.(2)}` : `$${formatNumberV2(+data?.[series2Idx], true)}`,
              offset: [0, 8],
            },
            tooltip: {
              backgroundColor: 'rgba(50, 50, 50, 0.9)',
              padding: 16,
              formatter: ({ data }) =>
                `${data?.[demoIdx]}<br />Number: ${formatNumberV2(data?.[countsIdx])}<br />Percentage: ${formatPercent(
                  data?.[percIdx] * 100,
                  false
                )}<br />Average Value: ${+data?.[series2Idx]?.toFixed?.(3)}<br />Std Dev: ${data?.[
                  stddevIdx
                ]?.toFixed?.(3)}<br />RMSE: ${data?.[rmseIdx]?.toFixed?.(3)}`,
            },
          };
    const seriesPricing =
      modelType === 'CLASSIFIER'
        ? null
        : {
            symbolSize: 24,
            symbol: 'roundRect',
            yAxisIndex: showValue ? 1 : 0,
            type: showValue ? 'scatter' : 'bar',
            seriesLayoutBy: 'row',
            name: dataset.dimensions[showValue ? 3 : 6],
            itemStyle: {
              color: brand100,
              barBorderRadius: [5, 5, 0, 0],
            },
            encode: {
              y: showValue ? 3 : 6,
            },
            tooltip: {
              rich: {
                bold: {
                  fontWeight: 800,
                },
              },
              backgroundColor: 'rgba(50, 50, 50, 0.9)',
              padding: 16,
              formatter: ({ data }) => {
                const value = +data?.[showValue ? 3 : 6];
                const formatted = showValue ? `$${formatNumberV2(value?.toFixed?.(2))}` : value?.toFixed?.(3);
                return `${data?.[demoIdx]}<br />Number: ${formatNumberV2(
                  data?.[countsIdx]
                )}<br />Percentage: ${formatPercent(data?.[percIdx] * 100, false)}<br />Average Value: ${formatted}`;
              },
            },
            label: {
              show: true,
              position: showValue ? 'right' : 'insideTop',
              formatter: (series) =>
                `{${showValue ? 'bold|$' : 'empty|'}${
                  showValue ? formatNumberV2(+series.value[3], true) : +series.value[6]?.toFixed?.(2)
                }}`,
              offset: [0, 8],
              rich: {
                bold: {
                  fontWeight: 800,
                  fontSize: 14,
                  color: '#000000',
                },
                empty: {
                  fontSize: 14,
                  color: '#ffffff',
                },
              },
            },
          };
    return {
      dataset: {
        dimensions: dataset.dimensions,
        source: outData,
      },
      grid: { containLabel: true },
      legend: {
        show: true,
        textStyle: { fontWeight: 600, color: '#AFAFAF' },
        left: 128,
        top: 'bottom',
        padding: [18, 24],
        itemGap: 40,
        icon: 'roundRect',
      },
      series: [
        series1,
        series2,
        showPricing ? seriesPricing : null,
        !showValue
          ? {
              name: 'AIR Cutoff',
              type: 'line',
              lineStyle: {
                color: '#666666',
                type: 'solid',
                width: 3,
              },
              itemStyle: {
                color: '#666666',
                symbol: 'none',
                width: 3,
              },
              symbol: 'none',
              markLine: {
                symbol: 'none',
                silent: true,
                lineStyle: {
                  color: '#666666',
                  type: 'solid',
                  width: 3,
                },
                data: [
                  {
                    yAxis: minVal,
                  },
                  {
                    yAxis: maxVal,
                  },
                ],
              },
              data: [],
            }
          : null,
      ].filter((elem) => elem),
      tooltip: {
        show: true,
        trigger: 'item',
      },
      xAxis: {
        type: 'category',
        axisLabel: {
          show: true,
          interval: 0,
          formatter: (value) => {
            const idx = dataset.source[0].findIndex((val) => val === value);
            const style = [0, 4, 9].includes(idx) ? 'control|' : 'treatment|';
            return `{${style}${value}}`;
          },
          fontSize: 14,
          margin: 8,
          rich: {
            control: {
              fontWeight: 800,
              borderWidth: 1,
              // borderType: 'solid',
              borderColor: '#000000',
              borderRadius: 4,
              color: '#333333',
              verticalAlign: 'bottom',
              padding: [4, 6, 5, 6],
            },
            treatment: {
              color: '#969696',
              fontWeight: 600,
              lineHeight: 24,
            },
          },
        },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: [
        {
          name: showValue ? yAxisName : 'Impact Ratios',
          nameGap: 64,
          nameTextStyle: {
            fontSize: 18,
            color: '#666666',
          },
          position: 'left',
          nameLocation: 'middle',
          min: 0,
          max: maxY,
          maxInterval: maxY ? 10 ** (maxY?.toFixed?.(0).length - 1) / 2 : null,
          axisLabel: {
            show: true,
            formatter: (value) => (showDecimal ? value?.toFixed?.(3) : `$${formatNumberV2(value, true)}`),
            color: '#969696',
            fontWeight: 'bold',
          },
          axisTick: { show: false },
          axisLine: { show: false },
        },
        {
          show: showPricing && showValue,
          name: 'Average Premium',
          position: 'right',
          nameGap: 64,
          nameTextStyle: {
            fontSize: 18,
            color: '#666666',
          },
          min: 0,
          max: maxY,
          maxInterval: maxY ? 10 ** (maxY?.toFixed?.(0).length - 1) / 2 : null,
          splitLine: { show: false },
          nameLocation: 'middle',
          axisLabel: {
            show: true,
            formatter: (value) => `$${formatNumberV2(+value, true)}`,
            color: '#969696',
            fontWeight: 'bold',
          },
          axisTick: { show: false },
          axisLine: { show: false },
        },
      ],
    };
  }, [
    model.dataModelType,
    dataset.source,
    dataset.dimensions,
    showValue,
    showPricing,
    hasPricing,
    minVal,
    maxVal,
    yAxisName,
    maxY,
    demoIndex,
    percIdx,
    stddevIdx,
    countsIdx,
    showDecimal,
    rmseIdx,
  ]);

  if (!dataset?.source?.length) return null;
  return (
    <Div m={{ t: '1rem' }} d="grid" flexGrow="1" borderColor="shadeD9" rounded="lg" border="1px solid">
      {dataset?.source?.length > 0 && (
        <ReactEcharts
          style={{ alignSelf: 'stretch', justifySelf: 'stretch', height: 'unset' }}
          notMerge
          option={options}
        />
      )}
    </Div>
  );
}

Infographic.propTypes = {
  model: ModelProp.isRequired,
  showPricing: PropTypes.bool.isRequired,
  showValue: PropTypes.bool.isRequired,
  demoIndex: PropTypes.number.isRequired,
  minVal: PropTypes.number.isRequired,
  maxVal: PropTypes.number.isRequired,
};
